import type { ProcessingActivityListParams } from 'config/api/processingActivities/processingActivities.types';
import DIRECTIONS from 'config/constants/sortDirections';
import processing_activities_messages from 'messages/processing_activities_messages';

export type ProcessingActivitiesStatusFilter = 'published' | 'all' | 'archived';

/** Product LIA chips mapped to `has_lia_gap` (+ `legal_basis`) per BE schema. */
export type ProcessingActivitiesLiaStatusFilter = '' | 'missing' | 'implemented' | 'not_lia';

export type ProcessingActivitiesFilterValues = {
  status: ProcessingActivitiesStatusFilter;
  processingRole: '' | 'controller' | 'processor';
  reviewPriority: '' | 'normal' | 'elevated' | 'high';
  legalBasis: string;
  liaStatus: ProcessingActivitiesLiaStatusFilter;
  hasDraft: '' | 'true' | 'false';
  hasDpiaIndication: '' | 'true' | 'false';
  hasActiveReviewRequest: '' | 'true' | 'false';
};

export type ProcessingActivitiesSort = {
  key: string | null;
  direction: string | null;
};

export const PROCESSING_ACTIVITIES_ALL_STATUSES = 'published,never_published,archived';

export const DEFAULT_PROCESSING_ACTIVITIES_FILTERS: ProcessingActivitiesFilterValues = {
  status: 'published',
  processingRole: '',
  reviewPriority: '',
  legalBasis: '',
  liaStatus: '',
  hasDraft: '',
  hasDpiaIndication: '',
  hasActiveReviewRequest: '',
};

export const PROCESSING_ROLE_FILTER_OPTIONS = ['controller', 'processor'] as const;

export const REVIEW_PRIORITY_FILTER_OPTIONS = ['high', 'elevated', 'normal'] as const;

export const LEGAL_BASIS_FILTER_OPTIONS = ['gdpr_6_1_a', 'gdpr_6_1_b', 'gdpr_6_1_c', 'gdpr_6_1_d', 'gdpr_6_1_e', 'gdpr_6_1_f'] as const;

export const LIA_STATUS_FILTER_OPTIONS = ['missing', 'implemented', 'not_lia'] as const;

export const BOOLEAN_FILTER_OPTIONS = ['true', 'false'] as const;

export const LIA_IMPLEMENTED_LEGAL_BASIS = 'gdpr_6_1_f';

export type ProcessingActivitiesTableFilterChip = {
  id: string;
  name: string | string[];
  tooltip?: string | string[];
};

export type ProcessingActivitiesTableFilterState = {
  legal_basis?: ProcessingActivitiesTableFilterChip[];
  lia_status?: ProcessingActivitiesTableFilterChip[];
  has_draft?: ProcessingActivitiesTableFilterChip[];
  has_dpia_indication?: ProcessingActivitiesTableFilterChip[];
  has_active_review_request?: ProcessingActivitiesTableFilterChip[];
};

export type ProcessingActivitiesTableAvailableFilters = {
  legal_basis: {
    sectionName: string[];
    filters: ProcessingActivitiesTableFilterChip[];
    isSingleSelect: boolean;
  };
  lia_status: {
    sectionName: string[];
    filters: ProcessingActivitiesTableFilterChip[];
    isSingleSelect: boolean;
  };
  has_draft: {
    sectionName: string[];
    filters: ProcessingActivitiesTableFilterChip[];
    isSingleSelect: boolean;
  };
  has_dpia_indication: {
    sectionName: string[];
    filters: ProcessingActivitiesTableFilterChip[];
    isSingleSelect: boolean;
  };
  has_active_review_request: {
    sectionName: string[];
    filters: ProcessingActivitiesTableFilterChip[];
    isSingleSelect: boolean;
  };
};

const legalBasisMessage = (code: string): string | string[] =>
  processing_activities_messages.legal_basis[code as keyof typeof processing_activities_messages.legal_basis] ?? code;

const booleanFilterMessage = (value: typeof BOOLEAN_FILTER_OPTIONS[number]): string | string[] =>
  processing_activities_messages.filters.boolean_options[value];

const liaStatusMessage = (value: typeof LIA_STATUS_FILTER_OPTIONS[number]): string | string[] =>
  processing_activities_messages.filters.lia_status_options[value];

const liaStatusTooltip = (value: typeof LIA_STATUS_FILTER_OPTIONS[number]): string | string[] | undefined => {
  if (value === 'missing') {
    return processing_activities_messages.filters.lia_status_missing_tooltip;
  }

  if (value === 'implemented') {
    return processing_activities_messages.filters.lia_status_implemented_tooltip;
  }

  return undefined;
};

export const buildProcessingActivitiesTableAvailableFilters = (): ProcessingActivitiesTableAvailableFilters => ({
  legal_basis: {
    sectionName: processing_activities_messages.filters.legal_basis_label,
    filters: LEGAL_BASIS_FILTER_OPTIONS.map(code => ({
      id: code,
      name: legalBasisMessage(code),
    })),
    isSingleSelect: true,
  },
  lia_status: {
    sectionName: processing_activities_messages.filters.lia_status_label,
    filters: LIA_STATUS_FILTER_OPTIONS.map(value => ({
      id: value,
      name: liaStatusMessage(value),
      ...(liaStatusTooltip(value) ? { tooltip: liaStatusTooltip(value) } : {}),
    })),
    isSingleSelect: true,
  },
  has_draft: {
    sectionName: processing_activities_messages.filters.draft_status_label,
    filters: BOOLEAN_FILTER_OPTIONS.map(value => ({
      id: value,
      name: booleanFilterMessage(value),
    })),
    isSingleSelect: true,
  },
  has_dpia_indication: {
    sectionName: processing_activities_messages.filters.dpia_indication_label,
    filters: BOOLEAN_FILTER_OPTIONS.map(value => ({
      id: value,
      name: booleanFilterMessage(value),
      ...(value === 'true' ? { tooltip: processing_activities_messages.filters.dpia_indication_yes_tooltip } : {}),
    })),
    isSingleSelect: true,
  },
  has_active_review_request: {
    sectionName: processing_activities_messages.filters.review_status_label,
    filters: BOOLEAN_FILTER_OPTIONS.map(value => ({
      id: value,
      name: booleanFilterMessage(value),
    })),
    isSingleSelect: true,
  },
});

export const processingActivitiesFilterValuesToTableFilters = (
  filters: ProcessingActivitiesFilterValues,
): ProcessingActivitiesTableFilterState => {
  const tableFilters: ProcessingActivitiesTableFilterState = {};

  if (filters.legalBasis) {
    tableFilters.legal_basis = [{ id: filters.legalBasis, name: legalBasisMessage(filters.legalBasis) }];
  }

  if (filters.liaStatus) {
    tableFilters.lia_status = [
      {
        id: filters.liaStatus,
        name: liaStatusMessage(filters.liaStatus),
        ...(liaStatusTooltip(filters.liaStatus) ? { tooltip: liaStatusTooltip(filters.liaStatus) } : {}),
      },
    ];
  }

  if (filters.hasDraft) {
    tableFilters.has_draft = [{ id: filters.hasDraft, name: booleanFilterMessage(filters.hasDraft) }];
  }

  if (filters.hasDpiaIndication) {
    tableFilters.has_dpia_indication = [{ id: filters.hasDpiaIndication, name: booleanFilterMessage(filters.hasDpiaIndication) }];
  }

  if (filters.hasActiveReviewRequest) {
    tableFilters.has_active_review_request = [
      { id: filters.hasActiveReviewRequest, name: booleanFilterMessage(filters.hasActiveReviewRequest) },
    ];
  }

  return tableFilters;
};

export const mergeTableFiltersIntoFilterValues = (
  filters: ProcessingActivitiesFilterValues,
  tableFilters: ProcessingActivitiesTableFilterState,
): ProcessingActivitiesFilterValues => ({
  ...filters,
  legalBasis: tableFilters.legal_basis?.[0]?.id ?? '',
  liaStatus: (tableFilters.lia_status?.[0]?.id as ProcessingActivitiesFilterValues['liaStatus']) ?? '',
  hasDraft: (tableFilters.has_draft?.[0]?.id as ProcessingActivitiesFilterValues['hasDraft']) ?? '',
  hasDpiaIndication: (tableFilters.has_dpia_indication?.[0]?.id as ProcessingActivitiesFilterValues['hasDpiaIndication']) ?? '',
  hasActiveReviewRequest:
    (tableFilters.has_active_review_request?.[0]?.id as ProcessingActivitiesFilterValues['hasActiveReviewRequest']) ?? '',
});

const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  processingRole: 'processing_role',
  legalBasis: 'legal_basis',
  publishedVersionNumber: 'published_version_number',
  linkedAssetsCount: 'linked_assets_count',
  review: 'review_answered_count',
  dpiaIndicationsCount: 'dpia_indications_count',
  reviewPriority: 'review_priority',
  status: 'status',
  updatedAt: 'updated_at',
};

const parseBooleanFilter = (value: '' | 'true' | 'false'): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

export const buildSortParam = (sort: ProcessingActivitiesSort): string | undefined => {
  if (!sort.key || !sort.direction) return undefined;

  const field = SORT_FIELD_MAP[sort.key];
  if (!field) return undefined;

  return sort.direction === DIRECTIONS.DESC ? `-${field}` : field;
};

export const buildProcessingActivitiesListParams = ({
  filters,
  page,
  pageSize,
  search,
  sort,
}: {
  filters: ProcessingActivitiesFilterValues;
  page: number;
  pageSize: number;
  search: string;
  sort: ProcessingActivitiesSort;
}): ProcessingActivityListParams => {
  const params: ProcessingActivityListParams = {
    page,
    page_size: pageSize,
  };

  const normalizedSearch = search.trim();
  if (normalizedSearch) {
    params.search = normalizedSearch;
  }

  params.status = filters.status === 'all' ? PROCESSING_ACTIVITIES_ALL_STATUSES : filters.status;

  if (filters.processingRole) {
    params.processing_role = filters.processingRole;
  }

  if (filters.reviewPriority) {
    params.review_priority = filters.reviewPriority;
  }

  // LIA mapping (schema): missing → has_lia_gap=true; implemented → has_lia_gap=false + legal_basis=gdpr_6_1_f; not_lia → omit has_lia_gap.
  if (filters.liaStatus === 'missing') {
    params.has_lia_gap = true;
  } else if (filters.liaStatus === 'implemented') {
    params.has_lia_gap = false;
    params.legal_basis = LIA_IMPLEMENTED_LEGAL_BASIS;
  }

  if (filters.legalBasis && filters.liaStatus !== 'implemented') {
    params.legal_basis = filters.legalBasis;
  }

  const hasDraft = parseBooleanFilter(filters.hasDraft);
  if (hasDraft !== undefined) {
    params.has_draft = hasDraft;
  }

  const hasDpiaIndication = parseBooleanFilter(filters.hasDpiaIndication);
  if (hasDpiaIndication !== undefined) {
    params.has_dpia_indication = hasDpiaIndication;
  }

  const hasActiveReviewRequest = parseBooleanFilter(filters.hasActiveReviewRequest);
  if (hasActiveReviewRequest !== undefined) {
    params.has_active_review_request = hasActiveReviewRequest;
  }

  const sortParam = buildSortParam(sort);
  if (sortParam) {
    params.sort = sortParam;
  }

  return params;
};

export const hasProcessingActivitiesListFiltersApplied = (filters: ProcessingActivitiesFilterValues, search: string): boolean => {
  if (search.trim()) {
    return true;
  }

  return (Object.keys(DEFAULT_PROCESSING_ACTIVITIES_FILTERS) as Array<keyof ProcessingActivitiesFilterValues>).some(
    key => filters[key] !== DEFAULT_PROCESSING_ACTIVITIES_FILTERS[key],
  );
};
